import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProjectProperties } from '../../utils/api/api';
import toast from 'react-hot-toast';
import AssignPropertyModal from '../properties/AssignPropertyModal';
import UpdateStatusModal from './UpdateStatusModal';

const ProjectProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const { id } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage, setPropertiesPerPage] = useState(10);
  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [showStatusModal, setShowStatusModal] = useState(false);

  const handleUpdateStatus = (property) => {
    setSelectedProperty(property);
    setShowStatusModal(true);
  };

  const handleStatusUpdateSuccess = () => {
    getProperties();
  };

  const getProperties = async () => {
    try {
      setLoading(true);
      const payload = { page: currentPage, pageSize: propertiesPerPage, project_id: id };

      if (status) payload.status = status;
      if (searchTerm) payload.search = searchTerm;

      const response = await getProjectProperties(payload);

      if (response && response.properties) {
        setProperties(response.properties);
        setTotalPages(response.meta.totalPages);
      } else {
        console.error("Unexpected response structure:", response);
        toast.error('Unexpected response structure from server');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };  

  useEffect(() => {
    getProperties();
  }, [currentPage, id, status, searchTerm, propertiesPerPage]);

  const handleAssignProperty = (property) => {
    setSelectedProperty(property);
    setShowModal(true);
  };

  const handleAssignSuccess = () => {
    getProperties();
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="w-full bg-white rounded-lg border border-gray-200 p-3 mt-4">
      <p className="mb-2 text-black font-medium uppercase text-[16px]">Project Properties</p>
      <div className="flex mb-4 w-full justify-between items-center">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 px-3 py-1.5 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-150 ease-in-out w-full md:w-1/3 lg:w-1/4"
        />
        <div className="flex items-center gap-4">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 px-3 py-1.5 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-150 ease-in-out"
          >
            <option value="">Select Status</option>
            <option value="available">Available</option>
            <option value="under_contract">Under Contract</option>
            <option value="sold">Sold</option>
            <option value="preserved">Preserved</option>
            <option value="booked">Booked</option>
            <option value="reserved">Reserved</option>
          </select>
          <select
            value={propertiesPerPage}
            onChange={(e) => setPropertiesPerPage(Number(e.target.value))}
            className="border border-gray-300 px-3 py-1.5 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-150 ease-in-out"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white h-full flex-grow table-auto">
          <thead>
            <tr className="text-primary border-b pb-4 text-[14px] py-3 bg-[#F7F7F7] font-medium leading-normal">
              <th className="py-3 px-3 text-left w-1/5 whitespace-nowrap">No.</th>
              <th className="py-3 px-6 text-left w-1/5 whitespace-nowrap">Title Number</th>
              <th className="py-3 px-6 text-left w-1/5 whitespace-nowrap">Property Number</th>
              <th className="py-3 px-6 text-left w-1/5 whitespace-nowrap">Status</th>
              <th className="py-3 px-6 text-left w-1/5 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="text-primary text-[14px] font-light">
            {loading ? (
              <tr>
                <td colSpan="6" className="py-4 px-6 text-center text-gray-500">Loading, please wait</td>
              </tr>
            ) : properties.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 px-6 text-center text-gray-500">No data found</td>
              </tr>
            ) : (
              properties.map((property, index) => (
                <tr
                  key={index}
                  className={`text-[14px] font-light text-black border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <td className="py-4 px-3 text-left whitespace-nowrap">{index + 1.}</td>
                  <td className="py-4 px-6 text-left whitespace-nowrap">{property.title_number}</td>
                  <td className="py-4 px-6 text-left whitespace-nowrap">{property.property_number}</td>
                  <td className="py-4 px-6 text-left whitespace-nowrap">
                    <span
                      className={`${property.status === 'not_started' ? 'text-gray-500 bg-gray-100 py-1 px-2 rounded' :
                        property.status === 'under_contract' ? 'text-yellow-500 bg-yellow-100 py-1 px-2 rounded-full' :
                          property.status === 'available' ? 'text-green-500 bg-green-100 py-1 px-2 rounded-full' :
                            property.status === 'sold' ? 'text-red-500 bg-red-100 py-1 px-2 rounded-full' : 'text-black bg-white'
                        }`}
                    >
                      {property.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-left flex items-center gap-2 whitespace-nowrap">
                    {property.status === 'available' && (
                      <button
                        onClick={() => handleAssignProperty(property)}
                        className="bg-secondary text-white py-1 px-2 rounded-md text-sm"
                      >
                        Assign
                      </button>
                    )}
                    <button
                      onClick={() => handleUpdateStatus(property)}
                      className="bg-secondary text-white py-1 px-2 rounded-md text-sm"
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {properties.length > 0 && (
        <div className="w-full flex justify-end mt-4">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="text-[14px] flex mr-1 items-center text-primary font-light gap-2"
          >
            Prev
          </button>

          {totalPages > 5 && currentPage > 3 && (
            <>
              <button
                onClick={() => paginate(1)}
                className={`px-3 py-1.5 mx-1 text-[14px] font-medium ${currentPage === 1 ? 'bg-primary text-white' : 'text-primary'} rounded-md`}
              >
                1
              </button>
              <span className="px-3 py-1.5 mx-1 text-[14px] font-medium text-primary">...</span>
            </>
          )}

          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1;
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 1 && page <= currentPage + 1)
            ) {
              return (
                <button
                  key={page}
                  onClick={() => paginate(page)}
                  className={`px-3 py-1.5 mx-1 text-[14px] font-medium ${currentPage === page ? 'bg-primary text-white' : 'text-primary'} rounded-md`}
                >
                  {page}
                </button>
              );
            } else {
              return null;
            }
          })}

          {totalPages > 5 && currentPage < totalPages - 2 && (
            <>
              <span className="px-3 py-1.5 mx-1 text-[14px] font-medium text-primary">...</span>
              <button
                onClick={() => paginate(totalPages)}
                className={`px-3 py-1.5 mx-1 text-[14px] font-medium ${currentPage === totalPages ? 'bg-primary text-white' : 'text-primary'} rounded-md`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="text-[14px] flex ml-1 items-center text-primary font-light gap-2"
          >
            Next
          </button>
        </div>
      )}

      {showModal && (
        <AssignPropertyModal
          property={selectedProperty}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onAssignSuccess={handleAssignSuccess}
        />
      )}

      {showStatusModal && (
        <UpdateStatusModal
          property={selectedProperty}
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          onUpdateSuccess={handleStatusUpdateSuccess}
        />
      )}
    </div>
  );
};

export default ProjectProperties;
